import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useExecutiveSummary, useMyDashboards } from '../api/dashboards.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { DashboardFrame, Waiting, formatMoney } from '../components/DashboardFrame.tsx';
import type { DashboardKey } from '../api/types.ts';

/**
 * The executive dashboard: how the agency is doing in social work, compliance
 * and finance, on one screen.
 *
 * Every figure is counted as the person looking, through row security, so it is
 * the agency's figure only for someone who can see the whole agency. A figure
 * links to the list behind it when the viewer holds that dashboard too.
 */
export function ExecutiveDashboard() {
  return (
    <DashboardFrame
      dashboard="executive"
      title="How the agency is doing"
      subtitle="Social work, compliance and finance, counted across every client you can see."
    >
      <ExecutiveBody />
    </DashboardFrame>
  );
}

function ExecutiveBody() {
  const summary = useExecutiveSummary();
  const mine = useMyDashboards();
  const link = (key: DashboardKey, to: string) =>
    mine.data?.dashboards.includes(key) ? to : undefined;

  if (summary.error && !summary.data) {
    return (
      <LoadFailure
        what="The agency summary"
        error={summary.error}
        onRetry={() => void summary.refetch()}
        retrying={summary.isFetching}
        forbiddenIsSession={false}
      />
    );
  }
  if (summary.isPending) {
    return <Waiting>Counting across every client you can see. This takes a few seconds.</Waiting>;
  }

  const s = summary.data;
  return (
    <>
      <Staleness
        updatedAt={summary.dataUpdatedAt}
        isFetching={summary.isFetching}
        error={summary.error}
        onRefresh={() => void summary.refetch()}
      />
      <div className="grid gap-4 p-4 lg:grid-cols-3 lg:p-5">
        <Section title="Social work" note="This year’s visits against the quota.">
          <Figure value={s.active_clients} label="clients under guardianship" />
          <Figure
            value={s.visits_high_arrears}
            label="under half the visit pace"
            to={link('director', '/dashboards/director?band=High')}
            stress
          />
          <Figure
            value={s.visits_medium_arrears}
            label="under three-quarters of the pace"
            to={link('director', '/dashboards/director?band=Medium')}
          />
          <Figure value={s.visits_on_or_near_pace} label="on pace or close to it" />
          <Figure
            value={s.behind_with_no_case_manager}
            label="behind, with no case manager"
            to={link('director', '/dashboards/director?case_manager=none')}
            stress
          />
        </Section>

        <Section title="Compliance" note="Reports owed to the court.">
          <Figure
            value={s.reports_overdue}
            label="reports overdue"
            to={link('compliance', '/dashboards/compliance?urgency=1')}
            stress
          />
          <Figure
            value={s.reports_due_within_30_days}
            label="due within 30 days"
            to={link('compliance', '/dashboards/compliance?urgency=2')}
          />
          <Figure
            value={s.reports_filed_this_year}
            label="reports filed this year"
            to={link('compliance', '/dashboards/compliance?view=filed')}
          />
        </Section>

        <Section title="Finance" note="Monthly compensation not yet billed this month.">
          <Figure
            value={formatMoney(s.compensation_collectable_amount)}
            label="collectable this month"
            to={link('finance', '/dashboards/finance')}
          />
          <Figure
            value={s.compensation_collectable}
            label="arrangements to bill"
            to={link('finance', '/dashboards/finance')}
          />
        </Section>
      </div>
    </>
  );
}

function Section({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-neutral-200">
      <header className="border-b border-neutral-200 px-4 py-3">
        <h2 className="text-lg font-bold text-neutral-900">{title}</h2>
        <p className="text-sm text-neutral-500">{note}</p>
      </header>
      <ul className="divide-y divide-neutral-200">{children}</ul>
    </section>
  );
}

function Figure({
  value,
  label,
  to,
  stress = false,
}: {
  value: number | string;
  label: string;
  to?: string;
  stress?: boolean;
}) {
  const body = (
    <>
      <span className={`min-w-[5.5rem] text-right text-2xl font-bold tabular-nums ${
        stress && value !== 0 ? 'text-warning-fg' : 'text-neutral-900'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      <span className="text-[0.9375rem] text-neutral-700">{label}</span>
      {to && <span aria-hidden="true" className="ml-auto text-neutral-400">›</span>}
    </>
  );
  const row = 'flex min-h-[56px] items-center gap-4 px-4 py-2';
  return (
    <li>
      {to ? (
        <Link to={to} className={`${row} hover:bg-neutral-50 active:bg-neutral-100`}>{body}</Link>
      ) : (
        <div className={row}>{body}</div>
      )}
    </li>
  );
}
